import React, { useState, useRef, useEffect } from 'react';
import { Filters, SetOption } from '../pages/CardBrowser';
import { useManaSymbols } from '../hooks/useManaSymbols';
import './FilterDrawer.css';

interface Props {
  filters: Filters;
  sets: SetOption[];
  onChange: (f: Filters) => void;
  onPinChange: (pinned: boolean) => void;
}

// Colour pie — W top, clockwise: U, B, R, G, C center
// Positions within 88x88 container, transformed via translate(-50%,-50%)
const PIE_DOTS = [
  { code: 'W', label: 'White',     bg: '#e8e0c8', left: 44,  top: 6   },
  { code: 'U', label: 'Blue',      bg: '#4a90d9', left: 82,  top: 30  },
  { code: 'B', label: 'Black',     bg: '#7a5a8a', left: 68,  top: 78  },
  { code: 'R', label: 'Red',       bg: '#d05040', left: 20,  top: 78  },
  { code: 'G', label: 'Green',     bg: '#3a8a5a', left: 6,   top: 30  },
  { code: 'C', label: 'Colorless', bg: '#8a9aaa', left: 44,  top: 44  },
];

const RARITIES = [
  { code: 'mythic',   label: 'Mythic',   color: '#e8954a' },
  { code: 'rare',     label: 'Rare',     color: '#c8a45a' },
  { code: 'uncommon', label: 'Uncommon', color: '#8ab4c8' },
  { code: 'common',   label: 'Common',   color: '#9a9aaa' },
];

const TYPE_OPTIONS = [
  'Creature', 'Instant', 'Sorcery',
  'Enchantment', 'Artifact', 'Planeswalker',
  'Battle', 'Land',
];

const FORMAT_OPTIONS = [
  { value: 'standard',  label: 'Standard'  },
  { value: 'pioneer',   label: 'Pioneer'   },
  { value: 'modern',    label: 'Modern'    },
  { value: 'legacy',    label: 'Legacy'    },
  { value: 'vintage',   label: 'Vintage'   },
  { value: 'commander', label: 'Commander' },
  { value: 'pauper',    label: 'Pauper'    },
  { value: 'historic',  label: 'Historic'  },
  { value: 'explorer',  label: 'Explorer'  },
  { value: 'brawl',     label: 'Brawl'     },
];

const CMC_OPS = [
  { value: 'gte', label: '≥' },
  { value: 'gt',  label: '>' },
  { value: 'eq',  label: '=' },
  { value: 'lte', label: '≤' },
  { value: 'lt',  label: '<' },
];

const ALL_KEYWORDS = [
  'Absorb','Adapt','Affinity','Afflict','Afterlife','Aftermath','Amplify',
  'Annihilator','Ascend','Assist','Augment','Awaken','Backup','Banding',
  'Bargain','Battalion','Bestow','Blitz','Bloodrush','Bloodthirst','Boast',
  'Bolster','Bushido','Buyback','Cascade','Casualty','Champion','Changeling',
  'Cipher','Clash','Cleave','Companion','Conspire','Convoke','Cycling',
  'Daybound','Deathtouch','Defender','Delve','Demonstrate','Detain','Devoid',
  'Devour','Discover','Disturb','Double Strike','Dredge','Echo','Embalm',
  'Emerge','Enlist','Entwine','Epic','Equip','Escalate','Escape','Eternalize',
  'Evoke','Evolve','Exalted','Exploit','Exert','Extort','Fabricate','Fading',
  'Fear','First Strike','Flash','Flashback','Flying','Forecast','Foretell',
  'Fortify','Frenzy','Fuse','Goad','Haste','Haunt','Hexproof','Hideaway',
  'Improvise','Indestructible','Infect','Inspire','Intimidate','Jump-start',
  'Kicker','Landfall','Lifelink','Madness','Menace','Miracle','Modular',
  'Morph','Mutate','Myriad','Ninjutsu','Offering','Outlast','Overload',
  'Partner','Persist','Phasing','Poisonous','Populate','Protection','Provoke',
  'Prowess','Prowl','Rampage','Reach','Rebound','Reconfigure','Recover',
  'Reinforce','Renown','Replicate','Retrace','Riot','Ripple','Scavenge',
  'Shadow','Shroud','Skulk','Soulbond','Soulshift','Splice','Storm',
  'Sunburst','Surge','Suspend','Totem Armor','Trample','Training',
  'Transform','Transmute','Tribute','Undaunted','Undying','Unearth',
  'Unleash','Vanishing','Venture','Vigilance','Ward','Wither',
].sort();

const DEFAULT_FILTERS: Filters = {
  colors: [], rarities: [], typeLine: '',
  setCodes: [], format: '', keywords: [], tags: [],
  produces: [], producesColorless: false,
  colorless: false,
  ownedOnly: false, showTokens: false,
  showDigital: false, showUnsets: false,
  showArtSeries: false, showNoiseSetTypes: false,
  minPrice: 0, maxPrice: 0, sortBy: 'name',
  cmcMin: 0, cmcMinOp: 'gte', cmcMax: 0, cmcMaxOp: 'lte',
  pipsW: 0, pipsU: 0, pipsB: 0, pipsR: 0, pipsG: 0, pipsC: 0,
  pipsGeneric: 0, pipsX: '',
  pipMode: 'all', pipCount: 'atleast',
  releaseYearFrom: 0, releaseYearTo: 0,
};

export default function FilterDrawer({ filters, sets, onChange, onPinChange }: Props) {
  const symbols = useManaSymbols();
  const [open,          setOpen]          = useState(false);
  const [setSearch,     setSetSearch]     = useState('');
  const [setDropOpen,   setSetDropOpen]   = useState(false);
  const [keywordSearch, setKeywordSearch] = useState('');
  const [tagSearch,     setTagSearch]     = useState('');
  const [pinned, setPinned] = useState(() => {
    try { return localStorage.getItem('filterPinned') === 'true'; } catch { return false; }
  });
  const drawerRef   = useRef<HTMLDivElement>(null);
  const triggerRef  = useRef<HTMLDivElement>(null);
  const setWrapRef  = useRef<HTMLDivElement>(null);
  const setInputRef = useRef<HTMLInputElement>(null);
  const closeTimer  = useRef<ReturnType<typeof setTimeout> | null>(null);

  useEffect(() => {
    onPinChange(pinned);
    if (pinned) setOpen(true);
  }, [pinned, onPinChange]);

  const openDrawer  = () => { if (closeTimer.current) clearTimeout(closeTimer.current); setOpen(true); };
  const closeDrawer = () => { if (pinned) return; closeTimer.current = setTimeout(() => setOpen(false), 200); };
  const cancelClose = () => { if (closeTimer.current) clearTimeout(closeTimer.current); };

  const togglePin = () => {
    const next = !pinned;
    setPinned(next);
    try { localStorage.setItem('filterPinned', String(next)); } catch {}
    if (!next) setOpen(false);
  };

  // Close drawer on outside click
  useEffect(() => {
    const handleClick = (e: MouseEvent) => {
      if (pinned) return;
      if (drawerRef.current && !drawerRef.current.contains(e.target as Node) &&
          triggerRef.current && !triggerRef.current.contains(e.target as Node)) {
        setOpen(false);
      }
    };
    document.addEventListener('mousedown', handleClick);
    return () => document.removeEventListener('mousedown', handleClick);
  }, [pinned]);

  // Close set dropdown on outside click
  useEffect(() => {
    const handleClick = (e: MouseEvent) => {
      if (setWrapRef.current && !setWrapRef.current.contains(e.target as Node)) {
        setSetDropOpen(false);
      }
    };
    document.addEventListener('mousedown', handleClick);
    return () => document.removeEventListener('mousedown', handleClick);
  }, []);

  // ── Colour pie toggles ────────────────────────────────────────────────────

  const isColorActive = (code: string) =>
    code === 'C' ? !!filters.colorless : filters.colors.includes(code);

  const toggleColor = (code: string) => {
    if (code === 'C') { onChange({ ...filters, colorless: !filters.colorless }); return; }
    const colors = filters.colors.includes(code)
      ? filters.colors.filter(c => c !== code)
      : [...filters.colors, code];
    onChange({ ...filters, colors });
  };

  const isProducesActive = (code: string) =>
    code === 'C' ? !!filters.producesColorless : (filters.produces || []).includes(code);

  const toggleProduces = (code: string) => {
    if (code === 'C') { onChange({ ...filters, producesColorless: !filters.producesColorless }); return; }
    const produces = (filters.produces || []).includes(code)
      ? (filters.produces || []).filter(c => c !== code)
      : [...(filters.produces || []), code];
    onChange({ ...filters, produces });
  };

  // ── Other toggles ─────────────────────────────────────────────────────────

  const toggleRarity = (code: string) => {
    const rarities = filters.rarities.includes(code)
      ? filters.rarities.filter(r => r !== code)
      : [...filters.rarities, code];
    onChange({ ...filters, rarities });
  };

  const toggleKeyword = (kw: string) => {
    const keywords = filters.keywords.includes(kw)
      ? filters.keywords.filter(k => k !== kw)
      : [...filters.keywords, kw];
    onChange({ ...filters, keywords });
  };

  const toggleTag = (tag: string) => {
    const tags = (filters.tags || []).includes(tag)
      ? (filters.tags || []).filter(t => t !== tag)
      : [...(filters.tags || []), tag];
    onChange({ ...filters, tags });
  };

  const addSet = (setCode: string, inputRef?: React.RefObject<HTMLInputElement | null>) => {
    if (!filters.setCodes.includes(setCode))
      onChange({ ...filters, setCodes: [...filters.setCodes, setCode] });
    setSetSearch('');
    setSetDropOpen(true);  // keep open — user may want to add another set
    // Return focus to input so continued typing works
    setTimeout(() => inputRef?.current?.focus(), 0);
  };

  const removeSet = (setCode: string) =>
    onChange({ ...filters, setCodes: filters.setCodes.filter(s => s !== setCode) });

  const toggleFormat = (fmt: string) =>
    onChange({ ...filters, format: filters.format === fmt ? '' : fmt });

  // Pip builder — left click +1, right click -1
  const adjustPip = (sym: string, delta: number) => {
    const key = `pips${sym}` as keyof Filters;
    const current = (filters[key] as number) || 0;
    onChange({ ...filters, [key]: Math.max(0, current + delta) });
  };

  const reset = () => {
    setSetSearch(''); setKeywordSearch(''); setTagSearch(''); setSetDropOpen(false);
    onChange({ ...DEFAULT_FILTERS, sortBy: filters.sortBy });
  };

  const hasFilters =
    filters.colors.length > 0 || filters.rarities.length > 0 ||
    filters.ownedOnly || filters.showTokens || filters.showDigital ||
    filters.showUnsets || filters.showArtSeries || filters.showNoiseSetTypes ||
    filters.typeLine || filters.setCodes.length > 0 || filters.format ||
    filters.keywords.length > 0 || (filters.tags || []).length > 0 ||
    (filters.produces || []).length > 0 || filters.producesColorless ||
    filters.colorless || filters.minPrice > 0 || filters.maxPrice > 0 ||
    (filters.cmcMin || 0) > 0 || (filters.cmcMax || 0) > 0 ||
    (filters.pipsW || 0) > 0 || (filters.pipsU || 0) > 0 ||
    (filters.pipsB || 0) > 0 || (filters.pipsR || 0) > 0 ||
    (filters.pipsG || 0) > 0 || (filters.pipsC || 0) > 0 ||
    (filters.pipsGeneric || 0) > 0 || !!(filters.pipsX || '') ||
    (filters.releaseYearFrom || 0) > 0 || (filters.releaseYearTo || 0) > 0;

  // Set dropdown — show all on focus, filter as you type, cap at 150 when empty
  const filteredSets = (() => {
    const q = setSearch.trim().toLowerCase();
    if (!q) return sets.slice(0, 150);
    // Strip spaces and punctuation from query for loose matching (e.g. "7ed" → "7ed")
    const qLoose = q.replace(/[^a-z0-9]/g, '');
    return sets.filter(s => {
      const name = s.name.toLowerCase();
      const code = s.setCode.toLowerCase();
      const nameLoose = name.replace(/[^a-z0-9]/g, '');
      // Exact substring on name or code
      if (name.includes(q) || code.includes(q)) return true;
      // Loose match — strip punctuation/spaces from both sides
      if (nameLoose.includes(qLoose) || code.replace(/[^a-z0-9]/g, '').includes(qLoose)) return true;
      return false;
    });
  })();

  const filteredKeywords = ALL_KEYWORDS.filter(kw =>
    !keywordSearch.trim() || kw.toLowerCase().includes(keywordSearch.toLowerCase())
  );

  // ── Colour pie renderer ───────────────────────────────────────────────────

  const renderPie = (
    isActive: (code: string) => boolean,
    onToggle: (code: string) => void
  ) => (
    <div className="pie-wrap">
      {PIE_DOTS.map(dot => {
        const token = `{${dot.code}}`;
        const sym   = symbols[token];
        const active = isActive(dot.code);
        return (
          <button
            key={dot.code}
            className={`pie-dot ${active ? 'active' : ''}`}
            style={{
              left: dot.left, top: dot.top,
              background: dot.bg,
            } as React.CSSProperties}
            onClick={() => onToggle(dot.code)}
            title={dot.label}
          >
            {sym?.imageUrl
              ? <img src={sym.imageUrl} alt={dot.code} width={16} height={16}
                     style={{ display: 'block', filter: active ? 'none' : 'grayscale(30%)' }} />
              : dot.code}
          </button>
        );
      })}
    </div>
  );

  // ── Render ────────────────────────────────────────────────────────────────

  return (
    <>
      <div
        ref={triggerRef}
        className={`filter-trigger ${open ? 'hidden' : ''}`}
        onMouseEnter={openDrawer}
        title="Open filters"
      >
        <div className="trigger-icon">⚙</div>
        {hasFilters && <div className="trigger-dot" />}
      </div>

      <div
        ref={drawerRef}
        className={`filter-drawer ${open ? 'open' : ''} ${pinned ? 'pinned' : ''}`}
        onMouseEnter={cancelClose}
        onMouseLeave={closeDrawer}
      >
        {/* ── HEADER ────────────────────────────────────────────────────── */}
        <div className="drawer-header">
          <div className="drawer-title">
            <span className="drawer-title-text">Filters</span>
            {hasFilters && <button className="drawer-reset" onClick={reset}>Reset all</button>}
          </div>
          <label className="header-owned-toggle">
            <input type="checkbox" checked={filters.ownedOnly}
              onChange={e => onChange({ ...filters, ownedOnly: e.target.checked })} />
            <span className="toggle-track"><span className="toggle-thumb" /></span>
            <span className="header-owned-label">Owned Only</span>
          </label>
          <div className="drawer-actions">
            <button className={`pin-btn ${pinned ? 'active' : ''}`} onClick={togglePin}
              title={pinned ? 'Unpin' : 'Pin open'}>📌</button>
            {!pinned && <button className="close-btn" onClick={() => setOpen(false)}>✕</button>}
          </div>
        </div>

        <div className="drawer-body">

          {/* ── COLOR (pie section) ──────────────────────────────────────── */}
          <div className="drawer-section">
            <div className="section-label">Color</div>
            <div className="pie-section-row">
              <div className="pie-col">
                <div className="pie-sub-label">Card Color</div>
                {renderPie(isColorActive, toggleColor)}
              </div>
              <div className="pie-col">
                <div className="pie-sub-label">Produces</div>
                {renderPie(isProducesActive, toggleProduces)}
              </div>
            </div>
            {((filters.produces || []).length > 0 || filters.producesColorless) && (
              <div className="filter-hint">Finds cards with "add &#123;X&#125;" in oracle text</div>
            )}
          </div>

          {/* ── MANA COST ────────────────────────────────────────────────── */}
          <div className="drawer-section">
            <div className="section-label">Mana Cost</div>

            <div className="cmc-row-label">CMC</div>
            <div className="cmc-row">
              <select className="cmc-op-select"
                value={filters.cmcMinOp || 'gte'}
                onChange={e => onChange({ ...filters, cmcMinOp: e.target.value })}>
                {CMC_OPS.map(op => <option key={op.value} value={op.value}>{op.label}</option>)}
              </select>
              <input type="number" className="cmc-input" placeholder="Min"
                min={0} max={20} value={filters.cmcMin || ''}
                onChange={e => onChange({ ...filters, cmcMin: Number(e.target.value) || 0 })} />
              <span className="cmc-sep">and</span>
              <select className="cmc-op-select"
                value={filters.cmcMaxOp || 'lte'}
                onChange={e => onChange({ ...filters, cmcMaxOp: e.target.value })}>
                {CMC_OPS.map(op => <option key={op.value} value={op.value}>{op.label}</option>)}
              </select>
              <input type="number" className="cmc-input" placeholder="Max"
                min={0} max={20} value={filters.cmcMax || ''}
                onChange={e => onChange({ ...filters, cmcMax: Number(e.target.value) || 0 })} />
            </div>
            <div className="filter-hint">X counts as 0 in CMC</div>

            <div className="cmc-row-label" style={{ marginTop: 8 }}>Pips</div>
            <div className="pip-mode-row">
              <div className="pip-mode-group">
                <button
                  className={`pip-mode-btn ${(filters.pipMode || 'all') === 'all' ? 'active' : ''}`}
                  onClick={() => onChange({ ...filters, pipMode: 'all' })}
                  title="Card must have ALL selected pip colours">
                  All of
                </button>
                <button
                  className={`pip-mode-btn ${filters.pipMode === 'any' ? 'active' : ''}`}
                  onClick={() => onChange({ ...filters, pipMode: 'any' })}
                  title="Card must have ANY of the selected pip colours">
                  Any of
                </button>
              </div>
              <div className="pip-mode-group">
                <button
                  className={`pip-mode-btn ${(filters.pipCount || 'atleast') === 'atleast' ? 'active' : ''}`}
                  onClick={() => onChange({ ...filters, pipCount: 'atleast' })}
                  title="Card must have at least this many pips">
                  At least
                </button>
                <button
                  className={`pip-mode-btn ${filters.pipCount === 'exact' ? 'active' : ''}`}
                  onClick={() => onChange({ ...filters, pipCount: 'exact' })}
                  title="Card must have exactly this many pips">
                  Exactly
                </button>
              </div>
            </div>
            <div className="pip-builder">
              {PIE_DOTS.map(dot => {
                const key = `pips${dot.code}` as keyof Filters;
                const count = (filters[key] as number) || 0;
                const token = `{${dot.code}}`;
                const mSym  = symbols[token];
                return (
                  <div key={dot.code} className="pip-slot">
                    <button
                      className={`pip-btn ${count > 0 ? 'active' : ''}`}
                      style={{ background: dot.bg } as React.CSSProperties}
                      onClick={() => adjustPip(dot.code, 1)}
                      onContextMenu={e => { e.preventDefault(); adjustPip(dot.code, -1); }}
                      title={`${dot.label} pips — left-click +1, right-click -1`}
                    >
                      {mSym?.imageUrl
                        ? <img src={mSym.imageUrl} alt={dot.code} width={15} height={15} />
                        : dot.code}
                    </button>
                    {count > 0 && <span className="pip-count">{count}</span>}
                  </div>
                );
              })}
            </div>

            <div className="pip-extra-row">
              <div className="pip-extra-item">
                <span className="pip-extra-label">Generic ≥</span>
                <input type="number" className="pip-extra-input" placeholder="0"
                  min={0} max={20} value={filters.pipsGeneric || ''}
                  onChange={e => onChange({ ...filters, pipsGeneric: Number(e.target.value) || 0 })} />
              </div>
              <div className="pip-extra-item">
                <span className="pip-extra-label">X cost</span>
                <select className="pip-x-select"
                  value={filters.pipsX || ''}
                  onChange={e => onChange({ ...filters, pipsX: e.target.value })}>
                  <option value="">Any</option>
                  <option value="X">X</option>
                  <option value="XX">XX</option>
                  <option value="XXX">XXX</option>
                </select>
              </div>
            </div>
          </div>

          {/* ── RARITY ───────────────────────────────────────────────────── */}
          <div className="drawer-section">
            <div className="section-label">Rarity</div>
            <div className="rarity-grid">
              {RARITIES.map(r => (
                <button key={r.code}
                  className={`rarity-pill ${filters.rarities.includes(r.code) ? 'active' : ''}`}
                  style={{ '--rc': r.color } as React.CSSProperties}
                  onClick={() => toggleRarity(r.code)}>
                  <span className="rarity-dot" style={{ background: r.color }} />
                  {r.label}
                </button>
              ))}
            </div>
          </div>

          {/* ── FORMAT ───────────────────────────────────────────────────── */}
          <div className="drawer-section">
            <div className="section-label">Format</div>
            <div className="format-pills">
              {FORMAT_OPTIONS.map(f => (
                <button key={f.value}
                  className={`format-pill ${filters.format === f.value ? 'active' : ''}`}
                  onClick={() => toggleFormat(f.value)}>
                  {f.label}
                </button>
              ))}
            </div>
          </div>

          {/* ── CARD TYPE ────────────────────────────────────────────────── */}
          <div className="drawer-section">
            <div className="section-label">Card Type</div>
            <div className="type-pills">
              {TYPE_OPTIONS.map(t => (
                <button key={t}
                  className={`type-pill ${filters.typeLine === t ? 'active' : ''}`}
                  onClick={() => onChange({ ...filters, typeLine: filters.typeLine === t ? '' : t })}>
                  {t}
                </button>
              ))}
            </div>
          </div>

          {/* ── SET ──────────────────────────────────────────────────────── */}
          <div className="drawer-section">
            <div className="section-label">Set</div>
            {filters.setCodes.length > 0 && (
              <div className="active-sets">
                {filters.setCodes.map(sc => {
                  const setInfo = sets.find(s => s.setCode === sc);
                  return (
                    <span key={sc} className="set-badge">
                      <span className="set-badge-code">{sc.toUpperCase()}</span>
                      <span className="set-badge-name">{setInfo?.name || sc.toUpperCase()}</span>
                      <button onClick={() => removeSet(sc)}>✕</button>
                    </span>
                  );
                })}
              </div>
            )}
            <div className="set-search-wrap" ref={setWrapRef}>
              <input
                ref={setInputRef}
                className="set-search-input"
                type="text"
                placeholder="Search sets…"
                value={setSearch}
                onChange={e => { setSetSearch(e.target.value); setSetDropOpen(true); }}
                onFocus={() => setSetDropOpen(true)}
                onKeyDown={e => {
                  if (e.key === 'Enter' && filteredSets.length > 0) {
                    e.preventDefault();
                    cancelClose();
                    addSet(filteredSets[0].setCode, setInputRef);
                  }
                  if (e.key === 'Escape') { setSetDropOpen(false); setSetSearch(''); }
                }}
              />
              {setDropOpen && filteredSets.length > 0 && (
                <div className="set-dropdown" onMouseDown={e => e.stopPropagation()}>
                  {filteredSets.map(s => (
                    <button
                      key={s.setCode}
                      className={`set-option ${filters.setCodes.includes(s.setCode) ? 'active' : ''}`}
                      onMouseDown={e => { e.preventDefault(); cancelClose(); addSet(s.setCode, setInputRef); }}
                    >
                      <span className="set-option-code">{s.setCode.toUpperCase()}</span>
                      <span className="set-option-name">{s.name}</span>
                      <span className="set-option-year">
                        {s.releasedAt ? new Date(s.releasedAt).getFullYear() : ''}
                      </span>
                    </button>
                  ))}
                </div>
              )}
            </div>
          </div>

          {/* ── PRICE ────────────────────────────────────────────────────── */}
          <div className="drawer-section">
            <div className="section-label">Price (USD)</div>
            <div className="price-row">
              <input type="number" className="price-input" placeholder="Min"
                min={0} value={filters.minPrice || ''}
                onChange={e => onChange({ ...filters, minPrice: Number(e.target.value) || 0 })} />
              <span className="price-sep">–</span>
              <input type="number" className="price-input" placeholder="Max"
                min={0} value={filters.maxPrice || ''}
                onChange={e => onChange({ ...filters, maxPrice: Number(e.target.value) || 0 })} />
            </div>
          </div>

          {/* ── RELEASE YEAR ─────────────────────────────────────────────── */}
          <div className="drawer-section">
            <div className="section-label">Release Year</div>
            <div className="price-row">
              <input type="number" className="price-input" placeholder="From"
                min={1993} max={2099} value={filters.releaseYearFrom || ''}
                onChange={e => onChange({ ...filters, releaseYearFrom: Number(e.target.value) || 0 })} />
              <span className="price-sep">–</span>
              <input type="number" className="price-input" placeholder="To"
                min={1993} max={2099} value={filters.releaseYearTo || ''}
                onChange={e => onChange({ ...filters, releaseYearTo: Number(e.target.value) || 0 })} />
            </div>
            <div className="filter-hint">e.g. 1993–2003 for old-border cards</div>
          </div>

          {/* ── KEYWORDS ─────────────────────────────────────────────────── */}
          <div className="drawer-section">
            <div className="section-label">Keywords</div>
            {filters.keywords.length > 0 && (
              <div className="active-keywords">
                {filters.keywords.map(kw => (
                  <span key={kw} className="keyword-badge">
                    {kw}<button onClick={() => toggleKeyword(kw)}>✕</button>
                  </span>
                ))}
              </div>
            )}
            <input className="set-search-input" type="text"
              placeholder="Search keywords…" value={keywordSearch}
              onChange={e => setKeywordSearch(e.target.value)} />
            <div className="keyword-list">
              {filteredKeywords.map(kw => (
                <button key={kw}
                  className={`keyword-option ${filters.keywords.includes(kw) ? 'active' : ''}`}
                  onClick={() => toggleKeyword(kw)}>
                  {kw}
                  {filters.keywords.includes(kw) && <span className="kw-check">✓</span>}
                </button>
              ))}
            </div>
          </div>

          {/* ── TAGS ─────────────────────────────────────────────────────── */}
          <div className="drawer-section">
            <div className="section-label">Tags</div>
            {(filters.tags || []).length > 0 && (
              <div className="active-keywords">
                {(filters.tags || []).map(tag => (
                  <span key={tag} className="tag-badge">
                    {tag}<button onClick={() => toggleTag(tag)}>✕</button>
                  </span>
                ))}
              </div>
            )}
            <input className="set-search-input" type="text"
              placeholder="Search tags…" value={tagSearch}
              onChange={e => setTagSearch(e.target.value)} />
            {tagSearch.trim() && (
              <button className="tag-add-btn"
                onClick={() => { toggleTag(tagSearch.trim()); setTagSearch(''); }}>
                + Add "{tagSearch.trim()}"
              </button>
            )}
          </div>

          {/* ── SHOW ALSO ────────────────────────────────────────────────── */}
          <div className="drawer-section" style={{ borderBottom: 'none' }}>
            <div className="section-label">Show Also</div>
            <div className="toggle-group">
              <Toggle label="Tokens"      checked={filters.showTokens}     onChange={v => onChange({ ...filters, showTokens: v })} />
              <Toggle label="Art Series"  checked={filters.showArtSeries}  onChange={v => onChange({ ...filters, showArtSeries: v })} />
              <Toggle label="Digital"     checked={filters.showDigital}    onChange={v => onChange({ ...filters, showDigital: v })} />
              <Toggle label="Un-sets"     checked={filters.showUnsets}     onChange={v => onChange({ ...filters, showUnsets: v })} />
              <Toggle label="Promos etc." checked={filters.showNoiseSetTypes} onChange={v => onChange({ ...filters, showNoiseSetTypes: v })} />
            </div>
          </div>

        </div>
      </div>

      {open && !pinned && (
        <div className="drawer-overlay" onClick={() => setOpen(false)} />
      )}
    </>
  );
}

function Toggle({ label, checked, onChange }: {
  label: string; checked: boolean; onChange: (v: boolean) => void;
}) {
  return (
    <label className="toggle">
      <input type="checkbox" checked={checked} onChange={e => onChange(e.target.checked)} />
      <span className="toggle-track"><span className="toggle-thumb" /></span>
      <span className="toggle-label">{label}</span>
    </label>
  );
}
